/**
 * Non-money formatting: quantities, percentages, counts, dates and times.
 *
 * Dates carry the tenancy rule from CLAUDE.md §5 in their signature. There are
 * two kinds of date in this product and conflating them is a real bug:
 *
 *  - **Business dates** (`invoiceDate`, `periodEnd`, `dueDate`) arrive as bare
 *    `YYYY-MM-DD`. They are calendar facts and must never be shifted by a
 *    timezone — an invoice dated 2026-01-31 is not 2026-01-30 for a viewer in
 *    Honolulu. `formatBusinessDate` renders them positionally, never converting.
 *  - **Instants** (`createdAt`, `postedAt`) arrive as RFC 3339 timestamptz and
 *    are rendered in the **tenant timezone** (registry field, default
 *    `Indian/Maldives`) unless the user has set a profile override.
 */

const DEFAULT_LOCALE = 'en'

/** Fallback tenant timezone; callers pass the registry value in practice. */
export const DEFAULT_TIME_ZONE = 'Indian/Maldives'

const BUSINESS_DATE = /^\d{4}-\d{2}-\d{2}$/

export type DatePreset = 'date' | 'dateShort' | 'dateLong' | 'dateTime' | 'time' | 'month'

const PRESETS: Record<DatePreset, Intl.DateTimeFormatOptions> = {
  date: { year: 'numeric', month: 'short', day: '2-digit' },
  dateShort: { year: '2-digit', month: '2-digit', day: '2-digit' },
  dateLong: { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' },
  dateTime: { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' },
  time: { hour: '2-digit', minute: '2-digit' },
  month: { year: 'numeric', month: 'short' },
}

export interface DateFormatOptions {
  locale?: string
  /** IANA identifier, e.g. `Indian/Maldives`. Ignored for business dates. */
  timeZone?: string
  preset?: DatePreset
  /** Escape hatch for a one-off shape; wins over `preset`. */
  options?: Intl.DateTimeFormatOptions
}

function formatterFor(locale: string, timeZone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone })
  } catch {
    // Unknown IANA zone (bad registry value): fall back to UTC rather than to
    // the viewer's local zone, so the wrong answer is at least a stable one.
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' })
  }
}

/** True for a bare `YYYY-MM-DD` business date. */
export function isBusinessDate(value: string): boolean {
  return BUSINESS_DATE.test(value)
}

/**
 * Render a bare `YYYY-MM-DD` without any timezone conversion, by pinning the
 * instant to UTC midnight and formatting in UTC.
 */
export function formatBusinessDate(value: string, opts: DateFormatOptions = {}): string {
  const { locale = DEFAULT_LOCALE, preset = 'date', options } = opts
  const instant = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(instant.getTime())) return ''
  const shape = options ?? PRESETS[preset === 'dateTime' || preset === 'time' ? 'date' : preset]
  return formatterFor(locale, 'UTC', shape).format(instant)
}

/**
 * Render a value as a date/time. Bare `YYYY-MM-DD` inputs are treated as
 * business dates and are never shifted; everything else is converted into
 * `timeZone`.
 */
export function formatDate(value: string | Date | null | undefined, opts: DateFormatOptions = {}): string {
  if (value === null || value === undefined || value === '') return ''

  if (typeof value === 'string' && isBusinessDate(value)) {
    return formatBusinessDate(value, opts)
  }

  const instant = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(instant.getTime())) return ''

  const { locale = DEFAULT_LOCALE, timeZone = DEFAULT_TIME_ZONE, preset = 'date', options } = opts
  return formatterFor(locale, timeZone, options ?? PRESETS[preset]).format(instant)
}

/** ISO 8601 instant string, or `''`. Useful for `<time datetime>`. */
export function toIsoString(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && isBusinessDate(value)) return value
  const instant = value instanceof Date ? value : new Date(value)
  return Number.isNaN(instant.getTime()) ? '' : instant.toISOString()
}

export interface NumberFormatOptions {
  locale?: string
  /** Fixed fraction digits. Quantities are NUMERIC(18,4) on the server. */
  decimals?: number
  minDecimals?: number
  maxDecimals?: number
  grouping?: boolean
  unit?: string
}

/**
 * Format a quantity or count. Unlike {@link formatMoney} this accepts a
 * `number`, because counts and quantities are not money — but a decimal string
 * is still preferred and is passed through Intl's string path so a
 * NUMERIC(18,4) quantity keeps every digit.
 */
export function formatNumber(
  value: string | number | null | undefined,
  opts: NumberFormatOptions = {},
): string {
  if (value === null || value === undefined || value === '') return ''

  const { locale = DEFAULT_LOCALE, decimals, minDecimals, maxDecimals, grouping = true, unit } = opts

  const min = decimals ?? minDecimals ?? 0
  const max = decimals ?? maxDecimals ?? Math.max(min, 3)

  const shape: Intl.NumberFormatOptions = {
    useGrouping: grouping,
    minimumFractionDigits: min,
    maximumFractionDigits: Math.max(min, max),
  }

  let text: string
  try {
    // ECMA-402 lets `format()` take a decimal string and formats it without
    // routing through a double, which is what keeps a NUMERIC(18,4) quantity
    // exact. The ES2022 lib typings predate that overload, hence the cast.
    const formatter = new Intl.NumberFormat(locale, shape) as unknown as {
      format(input: string | number): string
    }
    text = formatter.format(value)
  } catch {
    return String(value)
  }

  return unit ? `${text} ${unit}` : text
}

/** Format a ratio already expressed in percent units (e.g. `"12.5"` -> `12.5%`). */
export function formatPercent(
  value: string | number | null | undefined,
  opts: NumberFormatOptions = {},
): string {
  const text = formatNumber(value, { decimals: 1, ...opts })
  return text === '' ? '' : `${text}%`
}

/** Compact byte size for storage meters. */
export function formatBytes(bytes: number, locale = DEFAULT_LOCALE): string {
  if (!Number.isFinite(bytes)) return ''
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let size = Math.abs(bytes)
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  const sign = bytes < 0 ? '-' : ''
  const shown = new Intl.NumberFormat(locale, {
    maximumFractionDigits: unitIndex === 0 ? 0 : 1,
  }).format(size)
  return `${sign}${shown} ${units[unitIndex] ?? 'B'}`
}
